import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Link,
  Modal,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useStytch, useStytchSession } from "@stytch/react";
import { AccountSecurityMfa } from "features/account/AccountSecurityMfa";
import { AccountSecurityPassword } from "features/account/AccountSecurityPassword";
import {
  locationAfterLoginStorage,
  userStorage,
} from "features/auth/AuthUtils";
import { AuthPasswordForm } from "features/auth/password/AuthPasswordPage";
import {
  canStytchSessionResetPassword,
  isStytchMultiFactorLoginNeeded,
} from "features/auth/StytchUtils";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import { DeveloperZone } from "shared/components/developer/DeveloperZone";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { useForceUpdate } from "shared/hooks/useForceUpdate";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { useInterval } from "usehooks-ts";

export const AccountSecurityPage: React.FC = () => {
  const { t } = useTranslation(["common", "account", "auth"]);
  const location = useLocation();
  const navigate = useNavigate();
  const forceUpdate = useForceUpdate();

  const userData = userStorage.get()!;

  const stytchClient = useStytch();
  const { session: stytchSession } = useStytchSession();
  const hasRecentAuthFactor = canStytchSessionResetPassword(stytchSession);

  const {
    isOpen: isReauthenticateModalOpen,
    onOpen: openReauthenticateModal,
    onClose: closeReauthenticateModal,
  } = useDisclosure();

  // Refresh every 30s to check if stytch session has expired
  useInterval(forceUpdate, 30 * 1000);

  if (!stytchSession || !hasRecentAuthFactor) {
    return (
      <>
        <PageTabBody>
          <Alert status="info" mb={4} data-ls-enabled={true} data-public={true}>
            <AlertIcon>
              <InfoCircleIcon />
            </AlertIcon>
            <AlertDescription>
              {t("account:security.reauthenticateMessage")}
            </AlertDescription>
            <Link onClick={openReauthenticateModal}>
              {t("account:security.reauthenticate")}
            </Link>
          </Alert>
          <ContentBlockGroup>
            <ContentBlock isDisabled={true}>
              <ContentBlockHeader>
                <ContentBlockTitle>
                  {t("account:security.password.title")}
                </ContentBlockTitle>
              </ContentBlockHeader>
            </ContentBlock>
            <ContentBlock isDisabled={true}>
              <ContentBlockHeader>
                <ContentBlockTitle>
                  {t("account:security.mfa.title")}
                </ContentBlockTitle>
              </ContentBlockHeader>
            </ContentBlock>
          </ContentBlockGroup>
        </PageTabBody>
        <Modal
          isOpen={isReauthenticateModalOpen}
          onClose={closeReauthenticateModal}
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <AuthPasswordForm
              email={userData.email}
              onSuccess={() => {
                const newSession = stytchClient.session.getSync()!;
                if (isStytchMultiFactorLoginNeeded(newSession)) {
                  locationAfterLoginStorage.set(location);
                  navigate("/auth/continue");
                } else {
                  closeReauthenticateModal();
                }
              }}
              onCancel={closeReauthenticateModal}
            />
          </ModalContent>
        </Modal>
      </>
    );
  }
  return (
    <PageTabBody>
      <ContentBlockGroup defaultIndex={[0, 1]}>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("account:security.password.title")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody>
            <AccountSecurityPassword />
          </ContentBlockBody>
        </ContentBlock>
        <ContentBlock>
          <ContentBlockHeader>
            <ContentBlockTitle>
              {t("account:security.mfa.title")}
            </ContentBlockTitle>
          </ContentBlockHeader>
          <ContentBlockBody>
            <AccountSecurityMfa />
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      <DeveloperZone>
        <Button
          variant="subtle"
          onClick={() => {
            void stytchClient.session.revoke();
          }}
        >
          {t("auth:developer.revokeStytchSession")}
        </Button>
      </DeveloperZone>
    </PageTabBody>
  );
};
